//frontend/src/pages/Confirmation.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Typography, TextField, Button, Box, Paper, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useBooking } from '../context/BookingContext';
import axiosInstance from '../utils/axios';
import { useSettings } from '../hooks/useSettings';



export default function Confirmation() {
  const { t } = useTranslation();
  const { bookingData, setBookingData, setActiveStep } = useBooking();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const { formatPrice } = useSettings();

  useEffect(() => {
      if (!bookingData.service || !bookingData.specialist || !bookingData.dateTime) {
          navigate('/select-service');
          return;
      }
      setActiveStep(3);
      fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
        // Проверяем есть ли токен и его "похожесть" на JWT
        const token = localStorage.getItem('token');
        if (!token || token.split('.').length !== 3) {
            return; // Молча выходим если токена нет или он неправильного формата
        }

        const response = await axiosInstance.get('/auth/profile');
        setCurrentUser(response.data);
        setBookingData(prev => ({
            ...prev,
            clientInfo: {
                name: response.data.name || '',
                phone: response.data.phone || '',
                email: response.data.email || '',
                telegram: response.data.telegram || '',
                instagram: response.data.instagram || ''
            }
        }));
    } catch (error) {
        if (error.response?.status !== 401) {
            // Логируем только неожиданные ошибки, не 401
            console.error('Unexpected error fetching user data:', error);
        }
    }
};

  const handleSubmit = async (e) => {
   e.preventDefault();
   setIsLoading(true);
   setError(null);

   const appointmentData = {
       specialistId: bookingData.specialist._id,
       service: bookingData.service.name,
       startTime: bookingData.dateTime,
       duration: bookingData.service.duration,
       clientInfo: {
           name: bookingData.clientInfo.name,
           phone: bookingData.clientInfo.phone,
           email: bookingData.clientInfo.email,
           telegram: bookingData.clientInfo.telegram || '',
           instagram: bookingData.clientInfo.instagram || ''
       }
   };

   try {
       const response = await axiosInstance.post('/appointments', appointmentData);
       navigate('/dashboard');
   } catch (error) {
       setError(error.response?.data?.message || t('booking.confirmation.error'));
   } finally {
       setIsLoading(false);
   }
};

  const handleInputChange = (field) => (e) => {
      setBookingData(prev => ({
          ...prev,
          clientInfo: {
              ...prev.clientInfo,
              [field]: e.target.value
          }
      }));
  };

  const handleBack = () => {
      navigate(-1);
  };

  if (!bookingData.service || !bookingData.specialist || !bookingData.dateTime) {
      return null;
  }

  return (
      <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
              <Typography variant="h4" gutterBottom align="center">
                  {t('booking.confirmation.title')}
              </Typography>

              {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                  </Alert>
              )}

              <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom>
                      {t('booking.confirmation.details')}
                  </Typography>
                  <Typography>
                      {t('booking.confirmation.service')}: {bookingData.service.name}
                  </Typography>
                  <Typography>
                      {t('booking.services.duration')}: {bookingData.service.duration} {t('common.minutes')}
                  </Typography>
                  <Typography>{t('booking.services.price')}: {formatPrice(bookingData.service.price)}</Typography>
                  <Typography>
                      {t('booking.confirmation.specialist')}: {bookingData.specialist.name}
                  </Typography>
                  <Typography>
                      {t('booking.confirmation.dateTime')}: {bookingData.dateTime.toLocaleString('en-US', {
                          dateStyle: 'medium',
                          timeStyle: 'short'
                      })}
                  </Typography>
              </Box>

              <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                      fullWidth
                      required
                      label={t('common.name')}
                      value={bookingData.clientInfo.name}
                      onChange={handleInputChange('name')}
                      margin="normal"
                  />
                  <TextField
                      fullWidth
                      required
                      label={t('common.phone')}
                      value={bookingData.clientInfo.phone}
                      onChange={handleInputChange('phone')}
                      margin="normal"
                  />
                  <TextField
                      fullWidth
                      required
                      label={t('common.email')}
                      type="email"
                      value={bookingData.clientInfo.email}
                      onChange={handleInputChange('email')}
                      margin="normal"
                  />
                  <TextField
                      fullWidth
                      label={`${t('common.telegram')} (${t('common.optional')})`}
                      value={bookingData.clientInfo.telegram}
                      onChange={handleInputChange('telegram')}
                      margin="normal"
                  />
                  <TextField
                      fullWidth
                      label={`${t('common.instagram')} (${t('common.optional')})`}
                      value={bookingData.clientInfo.instagram}
                      onChange={handleInputChange('instagram')}
                      margin="normal"
                  />
                  
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                      <Button 
                          variant="outlined" 
                          onClick={handleBack}
                      >
                          {t('common.back')}
                      </Button>
                      <Button
                          type="submit"
                          variant="contained"
                          disabled={isLoading}
                      >
                          {isLoading ? t('common.confirmbooking') : t('common.confirmbooking')}
                      </Button>
                  </Box>
              </Box>
          </Paper>
      </Container>
  );
}